import React from 'react'
import { ICellRendererParams } from 'ag-grid-enterprise'
import { Button, Dropdown, Tag, Tooltip } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { TFunction, useTranslation } from 'react-i18next'
import { Match } from '../types'
import {
  AcceptanceStatus,
  formatAmount,
  formatAmountWithCurrency,
  getClassifications,
  getDifference,
  getMatchCurrencies,
  getWorstClassification,
  isAccepted,
  PaymentStatus
} from '../utils/reconciliationHelpers'
import { ClassificationDot, ClassificationIcons } from './ClassificationTag'

export interface ReconciliationGridContext {
  companyNameById: Map<string, string>
  // Approver id (Match.accepted.acceptedBy) -> display name, merged across the group's companies.
  userNameById: Map<number, string>
  // ISO code of the group's reporting currency (parent company's `currency`).
  groupCurrency: string
  onApproveEntry: (match: Match) => void
  onRevokeEntry: (match: Match) => void
}

// One dot only, showing the most severe classification on the entry.
export const ClassificationDotCellRenderer: React.FC<ICellRendererParams<Match>> = ({ data }) => {
  if (!data) return null
  return <ClassificationDot classification={getWorstClassification(data)} />
}

export const ClassificationCellRenderer: React.FC<ICellRendererParams<Match>> = ({ data }) => {
  if (!data) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <ClassificationIcons classifications={getClassifications(data)} />
    </span>
  )
}

const ACCEPTANCE_TAG: Record<AcceptanceStatus, { color: string; key: string; fallback: string }> = {
  open: { color: 'default', key: 'icReconciliation:acceptanceOpen', fallback: 'Avoin' },
  accepted: { color: 'green', key: 'icReconciliation:acceptedTag', fallback: 'Hyväksytty' },
  stale: { color: 'orange', key: 'icReconciliation:staleTag', fallback: 'Hyväksyntä vanhentunut' }
}

export const acceptanceStatusLabel = (status: AcceptanceStatus, t: TFunction) => {
  const { key, fallback } = ACCEPTANCE_TAG[status]
  return t(key, fallback)
}

// Renders the AcceptanceStatus token produced by the column's valueGetter.
export const AcceptanceStatusCellRenderer: React.FC<ICellRendererParams<Match, AcceptanceStatus>> = ({ value }) => {
  const { t } = useTranslation()
  if (!value || value === 'open') return null
  const { color } = ACCEPTANCE_TAG[value]
  const tag = (
    <Tag color={color} style={{ marginInlineEnd: 0 }}>
      {acceptanceStatusLabel(value, t)}
    </Tag>
  )
  if (value === 'stale') {
    return (
      <Tooltip
        title={t('icReconciliation:staleTooltip', 'Tiedot muuttuivat hyväksynnän jälkeen — käsittele uudelleen')}
      >
        {tag}
      </Tooltip>
    )
  }
  return tag
}

// Amount cell showing the reporting-currency figure (no currency code — it's in the header),
// with the original ledger-currency amount on a second line when the voucher was booked in a
// different currency (that line keeps its ISO code, since it's a different currency).
export const LedgerAmountCellRenderer =
  (
    getLedgerAmount: (match: Match) => number | undefined,
    getLedgerCurrency: (match: Match) => string | undefined
  ): React.FC<ICellRendererParams<Match, number, ReconciliationGridContext>> =>
  ({ data, value, context }) => {
    if (!data) return null
    const groupCurrency = context?.groupCurrency ?? ''
    const ledgerCurrency = getLedgerCurrency(data)
    const ledgerAmount = getLedgerAmount(data)
    const showLedger = !!ledgerCurrency && ledgerCurrency !== groupCurrency && ledgerAmount != null
    // Stack both figures inside the fixed-height cell: tight line-heights + a smaller second
    // line keep the two rows within the 36px row without clipping. flex-end mirrors the
    // numericColumn right-alignment (the wrapping div drops ag-grid's own text-align: right).
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'center',
          height: '100%',
          lineHeight: 1.15
        }}
      >
        <span>{formatAmount(value ?? undefined)}</span>
        {showLedger && (
          <span style={{ fontSize: 12, lineHeight: 1.1, color: '#8c8c8c' }}>
            ({formatAmountWithCurrency(ledgerAmount, ledgerCurrency)})
          </span>
        )}
      </div>
    )
  }

// Side A / side B currency. Collapses to one code when both sides share a currency (or one side
// is missing); the second code is shown smaller/muted.
export const CurrencyCellRenderer: React.FC<ICellRendererParams<Match, unknown, ReconciliationGridContext>> = ({
  data,
  context
}) => {
  if (!data) return null
  const [primary, secondary] = getMatchCurrencies(data, context?.groupCurrency ?? '')
  if (!primary) return null
  return (
    <span>
      {primary}
      {secondary && <span style={{ fontSize: 11, color: '#8c8c8c' }}> / {secondary}</span>}
    </span>
  )
}

// Difference in the reporting currency (no currency code — it's in the header).
export const DifferenceCellRenderer: React.FC<ICellRendererParams<Match>> = ({ data }) => {
  if (!data) return null
  const difference = getDifference(data)
  return (
    <span style={{ color: difference === 0 ? undefined : '#f5222d', fontWeight: difference === 0 ? 400 : 600 }}>
      {formatAmount(difference)}
    </span>
  )
}

const SIDE_STATUS_TAG: Record<PaymentStatus, { color: string; key: string; fallback: string }> = {
  paid: { color: 'success', key: 'icReconciliation:tablePaid', fallback: 'Maksettu' },
  open: { color: 'orange', key: 'icReconciliation:tableOpen', fallback: 'Avoinna' },
  noInvoice: { color: 'default', key: 'icReconciliation:sideStatusNoInvoice', fallback: 'Ei laskua' },
  noVoucher: { color: 'default', key: 'icReconciliation:sideStatusNoVoucher', fallback: 'Ei tositetta' }
}

export const sideStatusLabel = (status: PaymentStatus, t: TFunction) => {
  const { key, fallback } = SIDE_STATUS_TAG[status]
  return t(key, fallback)
}

// Renders the SidePaymentStatus token produced by the column's valueGetter.
export const SidePaymentStatusCellRenderer: React.FC<ICellRendererParams<Match, PaymentStatus>> = ({ value }) => {
  const { t } = useTranslation()
  if (!value) return null
  const { color } = SIDE_STATUS_TAG[value]
  return <Tag color={color}>{sideStatusLabel(value, t)}</Tag>
}

export const ActionsCellRenderer: React.FC<ICellRendererParams<Match, unknown, ReconciliationGridContext>> = ({
  data,
  context
}) => {
  const { t } = useTranslation()
  if (!data) return null
  const accepted = isAccepted(data)
  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          accepted
            ? {
                key: 'revoke',
                label: t('icReconciliation:revokeAction', 'Peru hyväksyntä'),
                onClick: () => context?.onRevokeEntry(data)
              }
            : {
                key: 'approve',
                label: t('icReconciliation:approveAction', 'Hyväksy rivi'),
                onClick: () => context?.onApproveEntry(data)
              }
        ]
      }}
    >
      <Button type="text" size="small" icon={<MoreOutlined />} onClick={event => event.stopPropagation()} />
    </Dropdown>
  )
}
